import { sv, VNode } from "@dhx/ts-common/dom";
import { uid } from "@dhx/ts-common/core";
import { ItemType, IBoxSize, ICoords, ILineConfig } from "@dhx/ts-diagram";
import { BaseItem } from "../BaseItem";
import { getMeta } from "../../helpers/baseMetaInfo";
import locale from "../../locales/en";

export class Line extends BaseItem {
	public config: ILineConfig;

	constructor(config: ILineConfig, defaults?) {
		super(config, defaults);
		this.id = this.config.id = config.id || uid();

		if (config.width) {
			config.width = parseFloat(config.width as any);
		}
		if (config.height) {
			config.height = parseFloat(config.height as any);
		}
		if (config.x) {
			config.x = parseFloat(config.x as any);
		}
		if (config.y) {
			config.y = parseFloat(config.y as any);
		}
		if (config.strokeWidth) {
			config.strokeWidth = parseFloat(config.strokeWidth as any);
		}
	}
	public getBaseType(): ItemType {
		return "line";
	}
	public getMetaInfo(): { [key: string]: string }[] {
		const meta = getMeta([{ type: "grid", label: locale.gridStep }]);
		meta.push({
			id: "strokeProps",
			type: "stroke",
			label: "Stroke",
			connector: true,
		});

		return meta;
	}
	public setDefaults(config: ILineConfig): ILineConfig {
		config.connectType = config.connectType === "curved" ? "elbow" : config.connectType || "elbow";
		config.stroke = config.stroke || "#CCC";
		config.strokeWidth = config.strokeWidth || 2;
		config.cornersRadius = config.cornersRadius || 0;

		if (config.title) {
			config.title = {
				fontSize: 14,
				lineHeight: 14,
				textAlign: "center",
				textVerticalAlign: "center",
				fontStyle: "normal",
				fontWeight: "normal",
				fontColor: "#4C4C4C",
				fill: "#FFF",
				draggable: true,
				editable: true,
				hidden: false,

				...config.title,
			};

			config.title.text = Array.isArray(config.title.text) ? config.title.text : [];
		}

		return config;
	}
	public getBox(): IBoxSize {
		const max = this.config.points.reduce(
			(m, p) => {
				m.x = Math.max(m.x, p.x);
				m.y = Math.max(m.y, p.y);
				return m;
			},
			{ x: 0, y: 0 }
		);
		const width = max.x - this.config.x;
		const height = max.y - this.config.y;

		const left = this.config.x;
		const right = left + width;
		const top = this.config.y;
		const bottom = top + height;

		return { left, right, top, bottom };
	}
	public render(): VNode {
		const { $selected, id, stroke, strokeWidth } = this.config;
		const linePoints = this._getPoints();
		const arrow = this._getArrowLine() || [];

		return sv(
			"g",
			{
				dhx_id: id,
				_key: id,
				class: `dhx_diagram_item dhx_diagram_line ${$selected ? "dhx_diagram_line--selected" : ""}`,
			},
			[
				sv("path", {
					class: this.getCss(),
					d: linePoints,
					fill: "none",
					"stroke-dasharray": this._getType(),
					"stroke-linejoin": "round",
					"stroke-width": strokeWidth,
					stroke,
				}),
				...arrow,
			]
		);
	}
	protected _getType(): string {
		if (this.config.strokeType) {
			this.config.type = this.config.strokeType;
		}
		if (this.config.type) {
			switch (this.config.type) {
				case "line":
					return "";
				case "dash":
					return "5, 5";
				default:
					return "";
			}
		}
	}
	protected _getPoints(): string {
		return this._getStringPoints();
	}
	protected _getStringPoints() {
		this.config.width = Math.abs(
			this.config.points[this.config.points.length - 1].x - this.config.points[0].x
		);
		this.config.height = Math.abs(
			this.config.points[this.config.points.length - 1].y - this.config.points[0].y
		);
		this.config.x = this.config.points[0].x;
		this.config.y = this.config.points[0].y;

		if (this.config.connectType === "straight" || this.config.connectType === "curved") {
			this.config.points = [this.config.points[0], this.config.points[this.config.points.length - 1]];

			if (this.config.connectType === "curved") {
				return this._getCurvedPoints(this.config);
			}
		}

		return (
			`M ${this.config.x},${this.config.y} ` +
			this.config.points
				.map((a: any) => {
					if (a.x1 && a.y1) {
						return `Q${a.x1},${a.y1} ${a.x},${a.y}`;
					} else {
						return "L " + a.x + "," + a.y;
					}
				})
				.join(" ")
		);
	}
	protected _getCurvedPoints(config: ILineConfig): string {
		const ratio = 0.5;
		const x1 = config.points[0].x;
		const x2 = config.points[1].x;
		const y1 = config.points[0].y;
		const y2 = config.points[1].y;
		const dx = (x2 - x1) * ratio;
		const c1 = x1 + dx;
		const c2 = x2 - dx;

		return `M${x1},${y1} C${c1},${y1} ${c2},${y2} ${x2},${y2}`;
	}
	protected _getArrowLine(): any[] {
		const p = this.config.points;
		const startArrow = this.config.backArrow;
		const endArrow = this.config.forwardArrow;

		if (startArrow || endArrow) {
			return [
				startArrow ? this._getArrow(p[1], p[0]) : null,
				endArrow ? this._getArrow(p[p.length - 2], p[p.length - 1]) : null,
			];
		}
	}
	protected _getArrow(from: ICoords, to: ICoords) {
		const xCatet = from.x - to.x;
		const yCatet = from.y - to.y;

		const infinity = xCatet === 0 && yCatet === 0;

		const hypo = Math.sqrt(xCatet ** 2 + yCatet ** 2);
		const ratio = 1 / hypo;

		const xAdd = xCatet * ratio;
		const yAdd = yCatet * ratio;

		const center = {
			x: to.x,
			y: to.y,
		};

		const w = 5;
		const h = 5;

		const r1x = to.x - w;
		const r1y = to.y - h;
		const r2x = to.x + w;
		const r2y = to.y - h;

		let angle = Math.atan((to.x - from.x) / (to.y - from.y)) * (-180 / 3.14);

		if (from.y > to.y) {
			angle += 180;
		}

		const extraConfig: any = {};

		if (!infinity) {
			extraConfig.transform = `translate(${xAdd} ${yAdd}) rotate(${angle},${center.x},${center.y})`;
		}

		return sv("path", {
			d: `M${r1x},${r1y} L${center.x},${center.y} L${r2x},${r2y} Z`,
			class: "dhx_diagram_arrow " + this.getCss(),
			"shape-rendering": "auto",
			stroke: this.config.stroke,
			fill: this.config.stroke,
			...extraConfig,
		});
	}
}
